import React, { useState, useEffect } from 'react';
import { getCostAnomalies, triggerAnomalySync } from '../../../services/cloudopsApi';
import { markCloudOpsStepComplete } from '../../../hooks/useCloudOpsOnboardingStatus';
import { useProject } from '../../../contexts/ProjectContext';
import { useFilter } from '../../../contexts/FilterContext';
import ProjectAssignmentSelect from '../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../components/WorkloadAssignmentSelect';

interface RootCause {
  service?: string;
  region?: string;
  usage_type?: string;
  linked_account_name?: string;
}

interface AnomalyImpact {
  max_impact: number;
}

interface Anomaly {
  anomaly_id: string;
  dimension_value?: string;
  severity: string;
  status: string;
  confidence_percent: number;
  resource_id?: string;
  description?: string;
  baseline_cost?: number;
  impact: AnomalyImpact;
  increase_percent: number;
  detected_at?: string;
  anomaly_start_date?: string;
  project_id?: string;
  workload_id?: string;
  root_causes?: RootCause[];
}

interface AnomalySummary {
  total_anomalies: number;
  high_severity: number;
  medium_severity: number;
  low_severity: number;
  unresolved: number;
  potential_impact: number;
}

const ANOMALY_PROJECT_ASSIGNMENTS_KEY = 'cloudopsAnomalyProjectAssignments';

const readAnomalyProjectAssignments = (): Record<string, string | null> => {
  try {
    const raw = localStorage.getItem(ANOMALY_PROJECT_ASSIGNMENTS_KEY);
    return raw ? JSON.parse(raw) as Record<string, string | null> : {};
  } catch {
    return {};
  }
};

const writeAnomalyProjectAssignments = (assignments: Record<string, string | null>) => {
  try {
    localStorage.setItem(ANOMALY_PROJECT_ASSIGNMENTS_KEY, JSON.stringify(assignments));
  } catch {
    // Ignore storage failures.
  }
};

const applyAnomalyProjectAssignments = (
  anomalies: Anomaly[],
  assignments: Record<string, string | null>
): Anomaly[] => anomalies.map((anomaly) => ({
  ...anomaly,
  project_id: Object.prototype.hasOwnProperty.call(assignments, anomaly.anomaly_id)
    ? assignments[anomaly.anomaly_id] ?? undefined
    : anomaly.project_id,
}));

const ANOMALY_WORKLOAD_ASSIGNMENTS_KEY = 'cloudopsAnomalyWorkloadAssignments';

const readAnomalyWorkloadAssignments = (): Record<string, string | null> => {
  try {
    const raw = localStorage.getItem(ANOMALY_WORKLOAD_ASSIGNMENTS_KEY);
    return raw ? JSON.parse(raw) as Record<string, string | null> : {};
  } catch {
    return {};
  }
};

const writeAnomalyWorkloadAssignments = (assignments: Record<string, string | null>) => {
  try {
    localStorage.setItem(ANOMALY_WORKLOAD_ASSIGNMENTS_KEY, JSON.stringify(assignments));
  } catch {
    // Ignore storage failures.
  }
};

const applyAnomalyWorkloadAssignments = (
  anomalies: Anomaly[],
  assignments: Record<string, string | null>
): Anomaly[] => anomalies.map((anomaly) => ({
  ...anomaly,
  workload_id: Object.prototype.hasOwnProperty.call(assignments, anomaly.anomaly_id)
    ? assignments[anomaly.anomaly_id] ?? undefined
    : anomaly.workload_id,
}));

const buildAnomalySummary = (anomalies: Anomaly[]): AnomalySummary => ({
  total_anomalies: anomalies.length,
  high_severity: anomalies.filter((anomaly) => anomaly.severity === 'HIGH').length,
  medium_severity: anomalies.filter((anomaly) => anomaly.severity === 'MEDIUM').length,
  low_severity: anomalies.filter((anomaly) => anomaly.severity === 'LOW').length,
  unresolved: anomalies.filter((anomaly) => anomaly.status !== 'Resolved').length,
  potential_impact: anomalies.reduce((sum, anomaly) => sum + (anomaly.impact?.max_impact || 0), 0),
});

const AnomaliesPage: React.FC = () => {
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [summary, setSummary] = useState<AnomalySummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState<string>('All');
  const [expandedAnomalies, setExpandedAnomalies] = useState<Record<string, boolean>>({});
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  const { selectedProjectId } = useProject();
  const { filterType, selectedId: filterId } = useFilter();
  const [projectAssignments, setProjectAssignments] = useState<Record<string, string | null>>(() => readAnomalyProjectAssignments());
  const [workloadAssignments, setWorkloadAssignments] = useState<Record<string, string | null>>(() => readAnomalyWorkloadAssignments());

  // Mark onboarding step as complete when visiting this page
  useEffect(() => {
    markCloudOpsStepComplete('check_anomalies');
  }, []);

  useEffect(() => {
    fetchAnomalies();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severityFilter, selectedProjectId, projectAssignments, workloadAssignments, filterType, filterId]);

  const fetchAnomalies = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await getCostAnomalies({
        severityFilter: severityFilter,
      });

      const withProjects = applyAnomalyProjectAssignments(data.anomalies || [], projectAssignments);
      const assignedAnomalies = applyAnomalyWorkloadAssignments(withProjects, workloadAssignments);
      const filteredAnomalies = selectedProjectId
        ? assignedAnomalies.filter((anomaly) => anomaly.project_id === selectedProjectId)
        : assignedAnomalies;

      setAnomalies(filteredAnomalies);
      setSummary(buildAnomalySummary(filteredAnomalies));
    } catch (err: unknown) {
      console.error('Error fetching anomalies:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch anomalies');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      await triggerAnomalySync();
      setSyncMessage('Anomaly sync started. Data will refresh shortly.');
      // Refresh after a short delay to pick up any fast results
      setTimeout(() => {
        fetchAnomalies();
        setSyncMessage(null);
      }, 10000);
    } catch (err: unknown) {
      setSyncMessage(err instanceof Error ? err.message : 'Failed to trigger sync');
    } finally {
      setSyncing(false);
    }
  };

  const toggleAnomaly = (anomalyId: string) => {
    setExpandedAnomalies(prev => ({
      ...prev,
      [anomalyId]: !prev[anomalyId]
    }));
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'HIGH':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800';
      case 'MEDIUM':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800';
      case 'LOW':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'Investigating':
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
      case 'Resolved':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
      case 'Acknowledged':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleProjectAssign = async (anomalyId: string, projectId: string | null) => {
    setProjectAssignments((prev) => {
      const next = { ...prev, [anomalyId]: projectId };
      writeAnomalyProjectAssignments(next);
      return next;
    });
  };

  const handleWorkloadAssign = async (anomalyId: string, workloadId: string | null) => {
    setWorkloadAssignments((prev) => {
      const next = { ...prev, [anomalyId]: workloadId };
      writeAnomalyWorkloadAssignments(next);
      return next;
    });
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Cost Anomalies</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Detect and investigate unusual cost patterns across your AWS infrastructure
          </p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          <svg className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {syncMessage && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p className="text-blue-700 dark:text-blue-300 text-sm">{syncMessage}</p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Anomalies</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {summary.total_anomalies}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">High Severity</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                  {summary.high_severity}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Unresolved</p>
                <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">
                  {summary.unresolved}
                </p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Potential Impact</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                  {formatCurrency(summary.potential_impact)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filter by Severity:</span>
          <div className="flex gap-2">
            {['All', 'High', 'Medium', 'Low'].map((filter) => (
              <button
                key={filter}
                onClick={() => setSeverityFilter(filter)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  severityFilter === filter
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {filter} {filter !== 'All' && summary && `(${
                  filter === 'High' ? summary.high_severity :
                  filter === 'Medium' ? summary.medium_severity :
                  summary.low_severity
                })`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Anomalies List */}
      <div className="space-y-4">
        {anomalies.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-12 text-center border border-gray-200 dark:border-gray-700">
            <svg className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600 dark:text-gray-400 text-lg">No anomalies found</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">Your costs are looking normal!</p>
          </div>
        ) : (
          anomalies.map((anomaly) => {
            const detectedDateDisplay = anomaly.detected_at
              ? formatDate(anomaly.detected_at)
              : anomaly.anomaly_start_date
              ? formatDate(anomaly.anomaly_start_date)
              : 'N/A';
            return (
            <div
              key={anomaly.anomaly_id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {anomaly.dimension_value || 'Unknown Service'}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getSeverityColor(anomaly.severity)}`}>
                        {anomaly.severity}
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(anomaly.status)}`}>
                        {anomaly.status}
                      </span>
                      <ProjectAssignmentSelect
                        currentProjectId={anomaly.project_id}
                        onAssign={(projectId) => handleProjectAssign(anomaly.anomaly_id, projectId)}
                      />
                      <WorkloadAssignmentSelect
                        currentWorkloadId={anomaly.workload_id}
                        onAssign={(workloadId) => handleWorkloadAssign(anomaly.anomaly_id, workloadId)}
                      />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {anomaly.confidence_percent}% confidence
                      </span>
                    </div>

                    {anomaly.resource_id && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {anomaly.resource_id}
                      </p>
                    )}

                    <p className="text-gray-700 dark:text-gray-300">
                      {anomaly.description || 'Cost anomaly detected'}
                    </p>

                    {/* Cost Impact Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Baseline Cost</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(anomaly.baseline_cost || 0)}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Actual Cost</p>
                        <p className="text-lg font-semibold text-red-600 dark:text-red-400">
                          {formatCurrency(anomaly.impact.max_impact)}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Increase</p>
                        <p className="text-lg font-semibold text-orange-600 dark:text-orange-400 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                          {anomaly.increase_percent > 0 ? `${anomaly.increase_percent}%` : 'N/A'}
                        </p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Impact</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(anomaly.impact.max_impact)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleAnomaly(anomaly.anomaly_id)}
                    className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View Details
                  </button>
                </div>

                <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  Detected: {detectedDateDisplay}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedAnomalies[anomaly.anomaly_id] && (
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-6">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Root Causes</h4>
                  {(anomaly.root_causes?.length ?? 0) > 0 ? (
                    <div className="space-y-2">
                      {anomaly.root_causes!.map((rc, idx) => (
                        <div key={`${rc.service}-${rc.region}-${idx}`} className="bg-white dark:bg-gray-800 rounded p-3 text-sm">
                          <div className="grid grid-cols-2 gap-2 text-gray-700 dark:text-gray-300">
                            {rc.service && <div><span className="font-medium text-gray-900 dark:text-white">Service:</span> {rc.service}</div>}
                            {rc.region && <div><span className="font-medium text-gray-900 dark:text-white">Region:</span> {rc.region}</div>}
                            {rc.usage_type && <div><span className="font-medium text-gray-900 dark:text-white">Usage Type:</span> {rc.usage_type}</div>}
                            {rc.linked_account_name && <div><span className="font-medium text-gray-900 dark:text-white">Account:</span> {rc.linked_account_name}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 dark:text-gray-400 text-sm">No detailed root cause information available</p>
                  )}
                </div>
              )}
            </div>
          );})
        )}
      </div>
    </div>
  );
};

export default AnomaliesPage;
